import type { TableNavigationState } from '../hooks/useChatTable'
import { MatterActivityFeedActions } from '@/matter/activity-feed/store/slice'
import { DEVICE_TYPE, useHeaderDeviceType } from '@/components/header/hooks/useHeaderDeviceType'
import { useDelayedVisibility } from '@/hooks/useDelayedVisibility'
import { TablesList } from './TablesList'
import CreateTableDialog from './CreateTableDialog'
import { SearchField } from '@/components/search-field/SearchField'
import { Plus, Retry } from '@/assets/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectMatterById } from '@/matter/store/selectors'
import { isActiveMatterMember } from '@/matter/utils/matter-permissions'
import { matterPaths } from '@/routing/paths'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { getTables } from '../fetch/get-tables'
import { postCreateTable } from '../fetch/post-create-table'
import { MatterTable } from '../schemas'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { TableCreationMethod, TableEntrySource } from '@/analytics/enums/table.enums'
import { trackTableCreated, trackTableOpened } from '@/analytics/utils/table-analytics'
import StandaloneTableWorkspace from './StandaloneTableWorkspace'
import { Header } from '@/components/header/Header'

export default function TablesPage() {
  const navigate = useNavigate()
  const showsMobileChrome = useHeaderDeviceType() !== DEVICE_TYPE.DESKTOP
  const { matterId, tableId } = useParams<{ matterId: string; tableId: string }>()
  const matter = useAppSelector((state) => (matterId ? selectMatterById(state, { matterId }) : undefined))
  if (!matterId || !isActiveMatterMember(matter)) return <p className="p-6">This matter is not available.</p>
  return tableId ? (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div id="tables-beta-banner-target" className="relative grow-0 shrink-0 w-full">
        <div id="tables-beta-banner-target-child" className="min-h-px w-full" />
      </div>
      <StandaloneTableWorkspace key={`${matterId}-${tableId}`} matterId={matterId} tableId={tableId} />
    </div>
  ) : (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      <div id="tables-beta-banner-target" className="relative grow-0 shrink-0 w-full">
        <div id="tables-beta-banner-target-child" className="min-h-px w-full" />
      </div>
      <Header
        borderBottom
        {...(showsMobileChrome
          ? { title: 'Tables' }
          : {
              breadcrumbs: [
                { id: matterId, label: matter?.name ?? 'Matter', onClick: () => navigate(matterPaths.overview(matterId)) },
                { id: 'tables', label: 'Tables' },
              ],
            })}
      />
      <TableList key={matterId} matterId={matterId} />
    </div>
  )
}

function TableList({ matterId }: { matterId: string }) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [tables, setTables] = useState<MatterTable[] | null>(null)
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const showsMobileChrome = useHeaderDeviceType() !== DEVICE_TYPE.DESKTOP
  const searchRef = useRef<HTMLInputElement>(null)
  const { trackEvent } = useAnalytics()
  const [error, setError] = useState<string | null>(null)
  const [refresh, setRefresh] = useState(0)
  const onChanged = useCallback(() => {
    dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
    setRefresh((value) => value + 1)
  }, [dispatch, matterId])
  const retry = () => {
    setError(null)
    onChanged()
  }
  const loading = tables === null && error === null
  const withinMinimumDisplay = useDelayedVisibility(loading, { showDelayMs: 0, minDisplayMs: 500 })
  useEffect(() => {
    const controller = new AbortController()
    getTables(matterId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setTables(result)
          setError(null)
        }
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Unable to load tables.')
      })
    return () => controller.abort()
  }, [matterId, refresh])
  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 px-4 pb-0 pt-6 lg:gap-6 lg:px-10">
      <div className="flex shrink-0 flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          {!showsMobileChrome && <h1 className="font-rethink-sans text-xl font-semibold leading-7 text-brand-neutral-950">Tables</h1>}
          <Button className="ml-auto" size="sm" variant="tonal" leadingIcon={<Plus />} onClick={() => setShowCreate(true)}>
            New table
          </Button>
        </div>
        <SearchField ref={searchRef} value={search} onChange={setSearch} placeholder="Search tables" aria-label="Search tables" />
      </div>
      {showCreate && (
        <CreateTableDialog
          onClose={() => setShowCreate(false)}
          onContinue={async (fromFiles) => {
            if (fromFiles) {
              navigate(matterPaths.createTable(matterId))
              return
            }
            const table = await postCreateTable(matterId, 'Untitled table')
            trackTableCreated(trackEvent, { tableId: table.id, matterId, tableCreationMethod: TableCreationMethod.Blank, documentCount: 0 })
            dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
            navigate(matterPaths.table(matterId, table.id), { state: { initialTable: table, openAssistant: true } satisfies TableNavigationState })
          }}
        />
      )}
      {error !== null && tables !== null && tables.length > 0 && (
        <InlineMessage variant="neutral">
          <span className="flex w-full items-center gap-2">
            Could not refresh tables. The list may be out of date.
            <Button variant="tonal" color="secondary" size="xs" className="ml-auto" leadingIcon={<Retry className="size-3" />} onClick={retry}>
              Retry
            </Button>
          </span>
        </InlineMessage>
      )}
      <TablesList
        tables={tables ?? []}
        matterId={matterId}
        search={search}
        isLoading={loading || withinMinimumDisplay}
        isError={error !== null}
        onClearSearch={() => {
          setSearch('')
          searchRef.current?.focus()
        }}
        onRetry={retry}
        onOpen={(table) => {
          trackTableOpened(trackEvent, {
            tableId: table.id,
            matterId,
            tableEntrySource: TableEntrySource.TableList,
            documentAnalysis: table.document_analysis,
          })
          navigate(matterPaths.table(matterId, table.id), { state: { initialTable: table } satisfies TableNavigationState })
        }}
        onCreate={() => setShowCreate(true)}
        onChanged={onChanged}
      />
    </section>
  )
}
